import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useUploadBlacklistFile } from '../api';

// The legacy form gated this to 1MB but the size check was commented out;
// we apply it explicitly here so server-side rejections become local UX.
const ALLOWED_EXT = /\.(txt|csv)$/i;
const MAX_BYTES = 1 * 1024 * 1024;

export function BulkBlacklistPage() {
  const navigate = useNavigate();
  const upload = useUploadBlacklistFile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Pick a .txt or .csv file to upload.');
      return;
    }
    if (!ALLOWED_EXT.test(file.name)) {
      toast.error('Only .txt or .csv files are supported.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than 1 MB.');
      return;
    }
    upload.mutate(file, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Bulk upload accepted');
        navigate('/voice-blacklist');
      },
      onError: () => toast.error('Could not upload the file.'),
    });
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Bulk Add Blacklist"
        description="Upload a list of mobile numbers — one per line in a .txt or .csv file."
        actions={
          <Button asChild variant="outline">
            <Link to="/voice-blacklist">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Upload blacklist file</CardTitle>
          <CardDescription>
            One mobile number per line, .txt or .csv, up to 1 MB. Existing entries
            are skipped — duplicates do not cause an error.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4 sm:max-w-md">
            <div className="grid gap-1.5">
              <Label htmlFor="blistFile">
                Blacklist file<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="blistFile"
                ref={fileRef}
                type="file"
                accept=".txt,.csv,text/plain,text/csv"
                onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')}
              />
              {fileName && (
                <p className="text-xs text-muted-foreground">
                  Selected: <span className="font-mono">{fileName}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={upload.isPending}>
                {upload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileUp className="h-4 w-4" />
                )}
                Upload
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/voice-blacklist">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
